const marketReferenceEpoch = () =>
  document.querySelector('script[data-pricelab-storefront-epoch]')?.dataset
    ?.pricelabStorefrontEpoch || '';

const marketReferencePlacement = (element) => {
  if (!element) return 'storefront';
  if (element.matches('[data-market-reference-value-strip]')) return 'pdp-value-prop';
  if (element.matches('.market-reference-row-action')) return 'pdp-size-selector';
  if (element.closest('cart-drawer, .cart-drawer')) return 'cart-drawer';
  if (element.closest('cart-items, .cart-items')) return 'cart';
  if (element.closest('.hero')) return 'homepage-hero';
  if (element.closest('product-info, .product__info-container')) return 'pdp-price';
  if (element.closest('.card-wrapper, .product-card-wrapper')) return 'product-card';
  return 'storefront';
};

const marketReferenceData = (element, overrides = {}) => ({
  storefrontEpoch: marketReferenceEpoch(),
  variantId: element?.dataset?.variantId || '',
  sku: element?.dataset?.variantSku || '',
  sellingPriceCents: Number(element?.dataset?.sellingPriceCents || 0),
  referencePriceCents: Number(element?.dataset?.referencePriceCents || 0),
  sourceCount: Number(element?.dataset?.sourceCount || 0),
  placement: marketReferencePlacement(element),
  ...overrides,
});

const publishMarketReferenceEvent = (name, element, overrides = {}) => {
  if (!window.Shopify?.analytics?.publish) return false;
  window.Shopify.analytics.publish(name, marketReferenceData(element, overrides));
  return true;
};

const marketReferenceImpressions = new Set();
const observedMarketReferenceElements = new WeakSet();

const recordMarketReferenceImpression = (element) => {
  if (!element || element.offsetParent === null) return false;
  const data = marketReferenceData(element);
  const key = `${data.variantId}:${data.placement}`;
  if (!data.variantId || marketReferenceImpressions.has(key)) return false;
  if (publishMarketReferenceEvent('crystal_market_reference_impression', element)) {
    marketReferenceImpressions.add(key);
    return true;
  }
  return false;
};

const marketReferenceObserver = 'IntersectionObserver' in window
  ? new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            entry.intersectionRatio >= 0.25 &&
            recordMarketReferenceImpression(entry.target)
          ) {
            marketReferenceObserver.unobserve(entry.target);
          }
        });
      },
      {threshold: 0.25},
    )
  : null;

const observeMarketReferences = () => {
  document
    .querySelectorAll(
      '[data-market-reference-value-strip], [data-market-reference-open], [data-market-reference-inline]:not(.market-reference-inline--compact)',
    )
    .forEach((element) => {
      if (observedMarketReferenceElements.has(element)) return;
      observedMarketReferenceElements.add(element);
      if (marketReferenceObserver) marketReferenceObserver.observe(element);
      else recordMarketReferenceImpression(element);
    });
};

let marketReferenceObservationScheduled = false;
const scheduleMarketReferenceObservation = () => {
  if (marketReferenceObservationScheduled) return;
  marketReferenceObservationScheduled = true;
  window.requestAnimationFrame(() => {
    marketReferenceObservationScheduled = false;
    observeMarketReferences();
  });
};

class MarketReferenceDrawer extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-market-reference-content]');
    this.panel = this.querySelector('.market-reference-drawer__panel');
    this.lastTrigger = null;
    this.activeVariantId = null;

    this.onDocumentClick = this.handleDocumentClick.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variantId = event?.data?.variant?.id;
        if (variantId) this.activeVariantId = String(variantId);
        scheduleMarketReferenceObservation();
      });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    if (this.unsubscribeVariant) this.unsubscribeVariant();
  }

  handleDocumentClick(event) {
    const openButton = event.target.closest('[data-market-reference-open]');
    if (openButton) {
      const section = openButton.closest('.shopify-section');
      if (section && !section.contains(this)) return;
      event.preventDefault();
      this.open(openButton.dataset.variantId, openButton);
      return;
    }
    const sourceLink = event.target.closest('[data-market-reference-source]');
    if (sourceLink) {
      const template = this.querySelector(
        `[data-market-reference-template="${CSS.escape(this.activeVariantId || '')}"]`,
      );
      publishMarketReferenceEvent(
        'crystal_market_reference_source_clicked',
        template,
        {
          placement: 'comparison-methodology',
          sourceName: sourceLink.dataset.sourceName || '',
          sourceKind: sourceLink.dataset.sourceKind || '',
        },
      );
      return;
    }
    if (event.target.closest('[data-market-reference-close]')) {
      this.close();
      return;
    }
    if (event.target.closest('[data-market-reference-method-open]')) {
      this.showMethod();
      return;
    }
    if (event.target.closest('[data-market-reference-method-back]')) {
      this.showSummary();
    }
  }

  handleKeydown(event) {
    if (this.hidden) return;
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...this.panel.querySelectorAll('button:not([hidden]), a[href]:not([hidden]), [tabindex]:not([tabindex="-1"]):not([hidden])')]
      .filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  open(variantId, trigger) {
    const requestedId = String(variantId || this.activeVariantId || '');
    const template = this.querySelector(`[data-market-reference-template="${CSS.escape(requestedId)}"]`);
    if (!template) return;
    this.activeVariantId = requestedId;
    this.lastTrigger = trigger || document.activeElement;
    this.content.replaceChildren(template.content.cloneNode(true));
    publishMarketReferenceEvent('crystal_market_reference_opened', template, {
      placement: marketReferencePlacement(trigger),
    });
    this.hidden = false;
    document.body.classList.add('market-reference-open');
    requestAnimationFrame(() => {
      this.classList.add('is-open');
      this.panel.focus();
    });
  }

  close() {
    if (this.hidden) return;
    this.classList.remove('is-open');
    document.body.classList.remove('market-reference-open');
    window.setTimeout(() => {
      this.hidden = true;
      this.content.replaceChildren();
    }, 220);
    if (this.lastTrigger?.focus) this.lastTrigger.focus();
  }

  showMethod() {
    const template = this.querySelector(
      `[data-market-reference-template="${CSS.escape(this.activeVariantId || '')}"]`,
    );
    publishMarketReferenceEvent(
      'crystal_market_reference_methodology_viewed',
      template,
      {placement: 'comparison-drawer'},
    );
    this.content.querySelector('[data-market-reference-summary]')?.setAttribute('hidden', '');
    this.content.querySelector('[data-market-reference-method]')?.removeAttribute('hidden');
    this.panel.scrollTo({ top: 0, behavior: 'smooth' });
  }

  showSummary() {
    this.content.querySelector('[data-market-reference-method]')?.setAttribute('hidden', '');
    this.content.querySelector('[data-market-reference-summary]')?.removeAttribute('hidden');
    this.panel.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

if (!customElements.get('market-reference-drawer')) {
  customElements.define('market-reference-drawer', MarketReferenceDrawer);
}

observeMarketReferences();
window.setTimeout(observeMarketReferences, 1000);
new MutationObserver(scheduleMarketReferenceObservation).observe(document.body, {
  childList: true,
  subtree: true,
});
